import { getFileExtension, getViewerType } from '@cognitoforms/file-viewer/src/file-viewer.types';
import type {
	EmbeddedFormFileViewerField,
	EmbeddedFormFileViewerManifest,
	FileViewerFile
} from '@cognitoforms/file-viewer/src/file-viewer.types';
import type { FileDataRef } from '@cognitoforms/types/server-types/model/file-data-ref';

export interface FileViewerHostFile extends Pick<FileDataRef, 'Id' | 'Name' | 'Size' | 'ContentType'> {
	downloadUrl: string;
	viewerUrl: string;
	thumbnailUrl?: string;
	requiresGeneration?: boolean;
	generatedFormId?: string;
	generatedFieldName?: string;
}

export interface FileViewerHost {
	id: string;
	getFileViewerFieldLabel: () => string;
	getFileViewerFiles: () => Promise<FileViewerHostFile[]>;
	isFileViewerVisible?: () => boolean;
}

export interface BuildFileViewerManifestOptions {
	includeHidden?: boolean;
}

export interface SignedDocumentAttachment {
	fieldId: string;
	fieldLabel: string;
	file: FileViewerHostFile;
	templateNumber?: number;
	signing?: unknown;
}

export type FileViewerFileGenerationResult = Pick<FileViewerFile, 'downloadUrl' | 'viewerUrl'>;
export type FileViewerFileResolver = (file: FileViewerFile) => FileViewerFileGenerationResult | null | undefined;

function buildFileViewerFileRow(
	hostFile: FileViewerHostFile,
	fieldId: string,
	fieldLabel: string,
	fieldIndex: number,
	globalIndex: number
): FileViewerFile {
	return {
		Id: hostFile.Id,
		Name: hostFile.Name,
		Size: hostFile.Size,
		ContentType: hostFile.ContentType,
		fileId: hostFile.Id,
		fileName: hostFile.Name,
		fileSize: hostFile.Size,
		contentType: hostFile.ContentType,
		fieldId,
		fieldLabel,
		fieldIndex,
		globalIndex,
		downloadUrl: hostFile.downloadUrl,
		viewerUrl: hostFile.viewerUrl,
		thumbnailUrl: hostFile.thumbnailUrl ?? hostFile.downloadUrl,
		extension: getFileExtension(hostFile.Name),
		viewerType: getViewerType({ Name: hostFile.Name, ContentType: hostFile.ContentType }),
		requiresGeneration: hostFile.requiresGeneration,
		generatedFormId: hostFile.generatedFormId,
		generatedFieldName: hostFile.generatedFieldName
	};
}

export async function buildFileViewerManifest(
	hosts: FileViewerHost[],
	options?: BuildFileViewerManifestOptions
): Promise<EmbeddedFormFileViewerManifest> {
	const includeHidden = options?.includeHidden ?? false;
	const fields: EmbeddedFormFileViewerField[] = [];
	const files: FileViewerFile[] = [];
	let globalIndex = 0;

	const visibleHosts = hosts.filter(host => includeHidden || (host.isFileViewerVisible ? host.isFileViewerVisible() : true));
	const hostFileLists = await Promise.all(visibleHosts.map(host => host.getFileViewerFiles()));

	visibleHosts.forEach((host, hostIndex) => {
		const fieldId = host.id;
		const fieldLabel = host.getFileViewerFieldLabel();
		const fieldFiles = hostFileLists[hostIndex].map((hostFile, fieldIndex) => {
			const row = buildFileViewerFileRow(hostFile, fieldId, fieldLabel, fieldIndex, globalIndex);
			globalIndex += 1;
			return row;
		});

		fields.push({
			fieldId,
			fieldLabel,
			files: fieldFiles
		});
		files.push(...fieldFiles);
	});

	return {
		formTitle: '',
		entryTitle: '',
		fields,
		files
	};
}

function upsertByFileId(files: FileViewerFile[], file: FileViewerFile): FileViewerFile[] {
	const existingIndex = files.findIndex(existing => existing.fileId === file.fileId);
	return existingIndex >= 0
		? files.map((existing, index) => (index === existingIndex ? file : existing))
		: [...files, file];
}

export function mergeFileIntoFileViewerManifest(
	manifest: EmbeddedFormFileViewerManifest,
	file: FileViewerFile
): EmbeddedFormFileViewerManifest {
	const files = upsertByFileId(manifest.files, file);

	let matchedGroup = false;
	const fields = manifest.fields.map(group => {
		if (group.fieldId !== file.fieldId) {
			return group.files.some(existing => existing.fileId === file.fileId)
				? { ...group, files: group.files.filter(existing => existing.fileId !== file.fileId) }
				: group;
		}

		matchedGroup = true;
		return { ...group, files: upsertByFileId(group.files, file) };
	});

	if (!matchedGroup) {
		fields.push({
			fieldId: file.fieldId,
			fieldLabel: file.fieldLabel,
			files: [file]
		});
	}

	return {
		...manifest,
		fields,
		files
	};
}

export function appendSignedDocumentAttachments(
	manifest: EmbeddedFormFileViewerManifest,
	attachments: SignedDocumentAttachment[]
): EmbeddedFormFileViewerManifest {
	let nextManifest = manifest;

	for (const attachment of attachments) {
		const existingGroup = nextManifest.fields.find(group => group.fieldId === attachment.fieldId);
		const fieldIndex = existingGroup ? existingGroup.files.length : 0;
		const globalIndex = nextManifest.files.length;

		const row: FileViewerFile = {
			...buildFileViewerFileRow(attachment.file, attachment.fieldId, attachment.fieldLabel, fieldIndex, globalIndex),
			templateNumber: attachment.templateNumber,
			signing: attachment.signing
		};

		nextManifest = mergeFileIntoFileViewerManifest(nextManifest, row);
	}

	return nextManifest;
}

export function resolveFileViewerFile(file: FileViewerFile, resolve: FileViewerFileResolver): FileViewerFile {
	if (!file.requiresGeneration || !file.fileId || !file.fieldId)
		return file;

	const resolved = resolve(file);
	if (!resolved)
		return file;

	return {
		...file,
		downloadUrl: resolved.downloadUrl,
		viewerUrl: resolved.viewerUrl,
		requiresGeneration: false
	};
}
